import { ReloadIcon } from '@diligentcorp/atlas-react-icons';
import {
  FullScreenWhiteSpace,
  TitleAndDescription,
} from '@issuemanager/apps/client/components';
import { paths } from '@issuemanager/apps/client/paths';
import { Button, useEventCallback } from '@mui/material';
import { Stack } from '@mui/system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppErrorHandler } from '../../contexts';
import { InternalError } from '../visual-assets/internalError';

interface Props {
  title?: string;
  description?: string;
  imageComponent?: React.JSX.Element;
  hasHomeButton?: boolean;
  hasReloadButton?: boolean;
}

function useNavigateSafe() {
  try {
    return useNavigate();
  } catch (error) {
    console.warn(
      'useNavigate called outside Router context, falling back to window.location',
      error,
    );
    return null;
  }
}

export function GeneralErrorUI({
  title,
  description,
  imageComponent = <InternalError />,
  hasHomeButton = false,
  hasReloadButton = false,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigateSafe();
  const { setError } = useAppErrorHandler();

  const defaultTitle = t('errors.general.title');
  const defaultDescription = t('errors.general.description');

  const goHome = useEventCallback(() => {
    setError(null);
    if (navigate) {
      navigate(paths.app.index);
    } else {
      window.location.href = paths.app.index;
    }
  });

  const reloadPage = useEventCallback(() => {
    window.location.reload();
  });

  return (
    <FullScreenWhiteSpace>
      <Stack
        direction='column'
        justifyContent='center'
        alignItems='center'
        spacing={2}
        gap={4}
        height='100%'
        padding='32px'
        data-testid='general-error-ui'
      >
        {imageComponent}
        <TitleAndDescription
          title={title ?? defaultTitle}
          description={description ?? defaultDescription}
        />
        {hasHomeButton && (
          <Button
            data-testid='go-home-button'
            variant='contained'
            onClick={goHome}
          >
            {t('form.goHomeButtonLabel')}
          </Button>
        )}
        {hasReloadButton && (
          <Button
            data-testid='reload-button'
            variant='contained'
            onClick={reloadPage}
            startIcon={<ReloadIcon />}
          >
            {t('form.reloadButtonLabel')}
          </Button>
        )}
      </Stack>
    </FullScreenWhiteSpace>
  );
}
